import { Button } from '@octopus-do/octopus-storybook'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { TemplateCopy } from '@store/reducers/templates'
import { ErrorNotification } from '@storeOld/reducers/notification'
import type React from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'redux-first-history'

import API from '@/api'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useAuth } from '@/hooks'

const Template: React.FC = () => {
  const dispatch = useDispatch()
  const id = useSelector(migrationProjectSelector.getProjectId)
  const { authenticated } = useAuth()

  const [loading, setLoading] = useState(false)

  const onClick = () => {
    if (loading) {
      return
    }

    setLoading(true)

    sendGtmEvent(
      GtmTriggerName.TEMPLATE_USE,
      'Click to use template in the header toolbar'
    )

    // A signed-in user gets a real, owned project straight away, placed by the
    // API: their own team when the template belongs to one they are in, their
    // personal workspace otherwise. An anonymous visitor keeps the legacy
    // route, which makes an unowned project they claim on first save.
    const copy = authenticated
      ? API.Template.use(id, {})
      : API.Template.copy(id)

    copy
      .then((res: TemplateCopy) => {
        // A template copy never passes through saveProjectSaga, so count it
        // here when the user is signed in. Anonymous copies stay trial and are
        // counted later, on the header-badge claim handled by saveProjectSaga.
        if (authenticated) {
          sendGtmEvent(GtmTriggerName.TEMPLATE_PR_SAVE)
        }

        dispatch(push(`/${res.id}`))
      })
      .catch((e: unknown) => {
        console.error(e)
        setLoading(false)

        // A template shared by link may be closed to this account (404), or
        // the copy may simply fail — either way the click must not end in
        // silence.
        dispatch(
          ErrorNotification('Failed to create a project from the template')
        )
      })
  }

  return (
    <Button
      size="medium"
      label="Use template"
      loading={loading}
      onClick={onClick}
    />
  )
}

export default Template
